import {type ReactNode, type SyntheticEvent, useState} from "react";
import {Box, IconButton, Paper, Stack, Tab, Tabs, Tooltip, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import AuditingTab from "./AuditingTab";
import LegalEntityManagementTab from "./LegalEntityManagementTab";
import LlmConfigTab from "./LlmConfigTab";
import UserManagementTab from "./UserManagementTab";
import UsageTab from "./UsageTab";
import BillingTab from "./BillingTab";

const TabPanel = ({children, activeTab, index}: {children: ReactNode; activeTab: number; index: number}) => (
    <Box
        role="tabpanel"
        id={`admin-tabpanel-${index}`}
        aria-labelledby={`admin-tab-${index}`}
        hidden={activeTab !== index}
        sx={{
            display: activeTab === index ? "block" : "none",
        }}
    >
        {children}
    </Box>
);

const AdminDashboard = () => {
    const {t: translate} = useTranslation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (_event: SyntheticEvent, value: number) => {
        setActiveTab(value);
    };

    return (
        <Box
            sx={(theme) => ({
                height: "100%",
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                px: {xs: 2, md: 4},
                py: {xs: 2, md: 3},
                background: `
                   radial-gradient(circle at top left, ${alpha(theme.palette.primary.light, theme.palette.mode === "light" ? 0.22 : 0.16)}, transparent 32%),
                   linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.06 : 0.1)} 45%, ${theme.palette.background.default} 100%)
                 `,
                fontFamily: theme.typography.fontFamily,
            })}
        >
            <Stack spacing={3}>
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 5,
                        p: {xs: 2.5, md: 4},
                        border: "1px solid",
                        borderColor: alpha(theme.palette.primary.dark, 0.16),
                        background: `linear-gradient(140deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main} 52%, ${alpha(theme.palette.secondary.main, 0.84)} 150%)`,
                        color: theme.palette.primary.contrastText,
                    })}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            right: -80,
                            top: -120,
                            width: 260,
                            height: 260,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.08)",
                        }}
                    />
                    <Stack direction={{xs: "column", lg: "row"}} justifyContent="space-between" spacing={3}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{position: "relative", zIndex: 1}}>
                            <Tooltip title={translate("admin.shared.backToChat")}>
                                <IconButton
                                    onClick={() => navigate("/")}
                                    sx={{
                                        color: "inherit",
                                        bgcolor: "rgba(255,255,255,0.08)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                    }}
                                >
                                    <ArrowBackRoundedIcon />
                                </IconButton>
                            </Tooltip>
                            <Typography
                                sx={(theme) => ({
                                    fontFamily: theme.typography.h4.fontFamily || theme.typography.fontFamily,
                                    fontWeight: 800,
                                    fontSize: {xs: 34, md: 48},
                                    lineHeight: 1,
                                    letterSpacing: -1.4,
                                })}
                            >
                                {translate("admin.dashboard.title")}
                            </Typography>
                        </Stack>
                    </Stack>
                </Paper>

                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 4,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        backgroundColor: alpha(
                            theme.palette.background.paper,
                            theme.palette.mode === "light" ? 0.72 : 0.9,
                        ),
                        backdropFilter: "blur(16px)",
                    })}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={(theme) => ({
                            px: {xs: 1, md: 2},
                            py: 1,
                            "& .MuiTab-root": {
                                fontFamily: theme.typography.fontFamily,
                                textTransform: "none",
                                fontWeight: 700,
                                minHeight: 52,
                                minWidth: 0,
                                borderRadius: 2.5,
                                px: 2,
                                mx: 0.5,
                                color: "text.secondary",
                                transition: "background-color 120ms ease, color 120ms ease, font-weight 120ms ease",
                            },
                            "& .Mui-selected": {
                                color: "text.primary",
                                fontWeight: 800,
                                bgcolor: (theme) =>
                                    alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.12 : 0.2),
                            },
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                        })}
                    >
                        <Tab
                            id="admin-tab-0"
                            aria-controls="admin-tabpanel-0"
                            label={translate("admin.dashboard.tabs.legalEntityManagement")}
                        />
                        <Tab
                            id="admin-tab-1"
                            aria-controls="admin-tabpanel-1"
                            label={translate("admin.dashboard.tabs.userManagement")}
                        />
                        <Tab
                            id="admin-tab-2"
                            aria-controls="admin-tabpanel-2"
                            label={translate("admin.dashboard.tabs.llmConfig")}
                        />
                        <Tab
                            id="admin-tab-3"
                            aria-controls="admin-tabpanel-3"
                            label={translate("admin.dashboard.tabs.usage")}
                        />
                        <Tab
                            id="admin-tab-4"
                            aria-controls="admin-tabpanel-4"
                            label={translate("admin.dashboard.tabs.auditing")}
                        />
                        <Tab
                            id="admin-tab-5"
                            aria-controls="admin-tabpanel-5"
                            label = {translate("admin.dashboard.tabs.billing")}
                        />
                    </Tabs>
                </Paper>

                <TabPanel activeTab={activeTab} index={0}>
                    <LegalEntityManagementTab />
                </TabPanel>

                <TabPanel activeTab={activeTab} index={1}>
                    <UserManagementTab />
                </TabPanel>

                <TabPanel activeTab={activeTab} index={2}>
                    <LlmConfigTab />
                </TabPanel>

                <TabPanel activeTab={activeTab} index={3}>
                    <UsageTab />
                </TabPanel>

                <TabPanel activeTab={activeTab} index={4}>
                    <AuditingTab />
                </TabPanel>

                <TabPanel activeTab={activeTab} index={5}>
                    <BillingTab />
                </TabPanel>
            </Stack>
        </Box>
    );
};

export default AdminDashboard;
